import React, { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import * as PropTypes from 'prop-types';

import Slider from 'ati-ui-react/components/Slider';
import Tooltip from '@components/tooltip';

import styles from './styles.modules.scss';
import i18nStrings from './strings';
import { useLocalized } from 'utils/useLocalized';
import { DataContext } from 'contexts';
import getUrlParameter from 'ati-utils/getUrlParameter';
import { urlParams } from 'logic/entities';

const PeriodSlider = ({locked, onChange}) => {
    const strings = useLocalized(i18nStrings);
    const { licenseInfo, isOutsideRussia } = useContext(DataContext);
    const [searchPeriod, setSearchPeriod] = useState('');
    const [periodTitle, setPeriodTitle] = useState('');

    const periodValues = useMemo(() => {
        return {
            [strings.period.day.short]: '1d',
            [strings.period.week.short]: '1w',
            [strings.period.month.short]: '1m',
            [strings.period.quarter.short]: '3m',
            [strings.period.halfYear.short]: '6m',
            [strings.period.year.short]: '1y'
        };
    }, [strings]);

    const handleChangePeriod = useCallback((value) => {
        setSearchPeriod(value);
        let beginDate = new Date();
        let title = '';
        switch (value) {
            case strings.period.day.short: {
                beginDate.setDate(beginDate.getDate() - 1);
                title = strings.period.day.full;
                break;
            }
            case strings.period.week.short: {
                beginDate.setDate(beginDate.getDate() - 7);
                title = strings.period.week.full;
                break;
            }
            case strings.period.month.short: {
                beginDate.setMonth(beginDate.getMonth() - 1);
                title = strings.period.month.full;
                break;
            }
            case strings.period.quarter.short: {
                beginDate.setMonth(beginDate.getMonth() - 3);
                title = strings.period.quarter.full;
                break;
            }
            case strings.period.halfYear.short: {
                beginDate.setMonth(beginDate.getMonth() - 6);
                title = strings.period.halfYear.full;
                break;
            }
            case strings.period.year.short: {
                beginDate.setFullYear(beginDate.getFullYear() - 1);
                title = strings.period.year.full;
                break;
            }
        }

        setPeriodTitle(title);
        onChange(beginDate.toISOString(), periodValues[value], title);
    }, [onChange, periodValues, strings]);

    const sliderJSX = useMemo(() => (
        <Slider
            options={Object.keys(periodValues)}
            selectedValue={searchPeriod}
            onChange={handleChangePeriod}
        />
    ), [periodValues, searchPeriod, handleChangePeriod]);

    const initPeriodValue = useCallback(() => {
        if ((!licenseInfo || !licenseInfo.searchAccessFinishUtc) && !isOutsideRussia) {
            handleChangePeriod(strings.period.month.short);
            return;
        }
        const period = getUrlParameter(urlParams.period);
        if (period) {
            const key = Object.keys(periodValues).find(key => periodValues[key] === period);
            if (key) {
                handleChangePeriod(key);
                return;
            }
        }
        handleChangePeriod(strings.period.month.short);
    }, [licenseInfo, strings, periodValues, isOutsideRussia, handleChangePeriod]);

    useEffect(() => {
        initPeriodValue();
    }, [initPeriodValue]);

    return (
        <div className={styles['element']}>
            <div className={styles['title']}>
                {`${strings.title} ${periodTitle}`}
            </div>
            {!locked ? sliderJSX : (
                <Tooltip
                    content={strings.lockTooltip}
                    maxWidth={230}
                >
                    <div className={styles['locked']}>
                        {sliderJSX}
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

export default PeriodSlider;

PeriodSlider.propTypes = {
    locked: PropTypes.bool,
    onChange: PropTypes.func
};
